import { memo } from "react";
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Filters } from "consts/onboarding";
import { FilterVariant } from "consts/filters";
import { withSx } from "ui/atoms/Modal";
import Tabs from "ui/atoms/Tabs";
import { BasicDialog } from "ui/molecules/Dialog";
import { Toolbar } from "ui/molecules/TableGrids";
import * as adapters from 'ui/widgets/Table';
import {
    FeatureFiltersProvider,
    FilterRenderTypes,
    useFilterRequestMapper,
    ValueStrategy
} from "ui/widgets/Filters";
import { Action, useApplicationTableResourceContext } from "../../ApplicationTableResourceProvider";
import { useOnboardingApplicationLogsTab, useOnboardingDataGrid } from "./hooks";
import { useOnboardingApplicationLogs, useOnboardingStepsLogs } from "./useOnboardingLogs";
import OnboardingLogsFilters from "./OnboardingLogsFilters";
import { Resource } from "./types";

const ViewOnboardingLogs = () => {
    const { value, onChange } = useOnboardingApplicationLogsTab();

    const { context, onClose } = useApplicationTableResourceContext();

    const getParamsForRequest = useFilterRequestMapper({
        filterTypes: new Map<FilterVariant, string[]>([
            [FilterVariant.DateRange, [
                Filters.updatedDateTime
            ]]
        ])
    });

    const applicationStepsLogs = useOnboardingStepsLogs({ value });
    const applicationLogs = useOnboardingApplicationLogs({ value });

    const fetchData = (searchQuery: URLSearchParams) => {
        const queryParams = getParamsForRequest(searchQuery);

        const applicationId = context?.row.pki;

        applicationStepsLogs.fetchOnboardingLogs({
            applicationId
        }, queryParams);

        applicationLogs.fetchOnboardingLogs({
            applicationId,
            resource: Resource.Applications
        }, queryParams);
    };

    const dataGridProps = {
        ...useOnboardingDataGrid(),
        sx: {
            minWidth: 650,
            height: 'calc(100vh - 245px)'
        },
        disableSelectionOnClick: true,
        pagination: false,
        components: {
            Toolbar
        },
        initialState: {
            sorting: {
                sortModel: [
                    {
                        field: Filters.updatedDateTime,
                        sort: 'desc' as const
                    }
                ]
            }
        }
    };

    return (
        <BasicDialog
            isOpen={Object.is(context?.action, Action.ViewOnboardingLogs)}
            onClose={onClose}
            titleSlot='History Logs'
            DialogProps={withSx({
                width: '100%'
            })}
        >
            <FeatureFiltersProvider
                fetchData={fetchData}
                adapters={{
                    useSearchParams: adapters.useSearchParamsNoop
                }}
                filtersConfig={[
                    {
                        filterId: Filters.updatedDateTime,
                        filterVariant: FilterVariant.DateRange,
                        inputs: [
                            {
                                label: 'Date From - To',
                                propName: 'startProps',

                            }
                        ],
                        icon: 'CalendarTodayOutlined',
                        adornment: 'startAdornment',
                        renderAs: FilterRenderTypes.DateRange,
                        meta: {
                            defaultValueStrategy: ValueStrategy.Empty,
                            initialValueStrategy: ValueStrategy.Empty,
                            changeValueStrategy: ValueStrategy.Empty
                        }
                    }
                ]}
            >
                <OnboardingLogsFilters />
                <Tabs
                    value={value}
                    onChange={onChange}
                    model={[
                        {
                            label: 'Application Steps Logs',
                            component: (
                                <DataGridPremium
                                    {...dataGridProps}
                                    {...applicationStepsLogs}
                                    defaultGroupingExpansionDepth={-1}
                                    initialState={{
                                        ...dataGridProps.initialState,
                                        rowGrouping: {
                                            model: ['resource']
                                        }
                                    }}
                                />
                            )
                        },
                        {
                            label: 'Application Logs',
                            component: (
                                <DataGridPremium
                                    {...dataGridProps}
                                    {...applicationLogs}
                                />
                            ),
                        }
                    ]}
                    TabPanelProps={{
                        sx: {
                            p: 0
                        }
                    }}
                    TabListContainerProps={{
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}
                    TabListProps={{
                        'aria-label': 'onboarding logs tabs',
                        variant: 'scrollable',
                        scrollButtons: 'auto'
                    }}
                />
            </FeatureFiltersProvider>
        </BasicDialog>
    );
};

export default memo(ViewOnboardingLogs);
